import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import accountsImageSrc from '../../../assets/images/accounts.svg';
import {accountsLearnMoreLink} from '../../../helpers/accountHelpers';
import {SearchAccountsDropdown} from '../../common/dropdowns/search/searchAccountsDropdown';

interface ContactDetailsNoAccountProps {
  onAddCardToAccount: HandlerOf<number>;
  onOpenCreateNewAccountForm: HandlerOf<string>;
}

/*
 * Style.
 */

const NoAccountsContainerDiv = styled.div`
  height: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  text-align: center;
  justify-content: center;
`;

const StyledImg = styled.img`
  width: 92px;
  margin-bottom: 24px;
`;

const StyledNoAccountsTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.veryLarge};
  line-height: 28px;
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledNoAccountsDescriptionDiv = styled.div`
  max-width: 220px;
  line-height: 21px;
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledButtonContainerDiv = styled.div`
  display: flex;
`;

const StyledButton = styled(Button)`
  margin: auto;
`;
const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
`;

const StyledLearnMoreIcon = styled(Icon)`
  margin-left: 6px;
`;

const StyledNewAccountIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledSearchAccountsDropdown = styled(SearchAccountsDropdown)`
  margin-bottom: -20px;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  margin-top: 24px;
`;

/*
 * Component.
 */

export const ContactDetailsNoAccount: FC<ContactDetailsNoAccountProps> = (props) => {
  const {onAddCardToAccount, onOpenCreateNewAccountForm} = props;

  const onLearnMoreClick = () => {
    openInBrowser(accountsLearnMoreLink);
  };

  return (
    <NoAccountsContainerDiv>
      <StyledImg alt="contact-details-no-account-image" src={accountsImageSrc} />
      <StyledNoAccountsTitleDiv>
        <Trans id="contact-details-no-account-title">No Account</Trans>
      </StyledNoAccountsTitleDiv>
      <StyledNoAccountsDescriptionDiv>
        <Trans id="contact-details-no-account-description">
          Add account to see account information in your inbox and automate workflows using account-based
          rules.
        </Trans>
      </StyledNoAccountsDescriptionDiv>
      <StyledButtonContainerDiv>
        <StyledButton onClick={onLearnMoreClick} variant={ButtonVariantsEnum.LINK}>
          <StyledButtonContentDiv>
            <Trans id="contact-details-no-account-learn-more-button">Learn more</Trans>
            <StyledLearnMoreIcon name="externalLinkSmall" />
          </StyledButtonContentDiv>
        </StyledButton>
      </StyledButtonContainerDiv>
      {renderAddToAccountButton(onAddCardToAccount, onOpenCreateNewAccountForm)}
    </NoAccountsContainerDiv>
  );
};

/*
 * Helpers.
 */

const renderAddToAccountButton = (
  onAddCardToAccount: HandlerOf<number>,
  onOpenCreateNewAccountForm: HandlerOf<string>,
) => (
  <StyledPopoverCoordinator
    renderButton={(isOpen) => (
      <StyledButtonContainerDiv>
        <StyledButton variant={ButtonVariantsEnum.PRIMARY} hasHover={isOpen}>
          <StyledButtonContentDiv>
            <StyledNewAccountIcon name="newAccountSmall" />
            <Trans id="contact-details-no-account-add-account-button">Add account</Trans>
          </StyledButtonContentDiv>
        </StyledButton>
      </StyledButtonContainerDiv>
    )}
    renderModal={(popoverProps) => (
      <StyledSearchAccountsDropdown
        popoverProps={popoverProps}
        placement="top"
        selectedAccountId={undefined}
        onConfirm={(accountId) => {
          popoverProps.onRequestClose();
          onAddCardToAccount(accountId);
        }}
        onNewAccountClick={onOpenCreateNewAccountForm}
      />
    )}
  />
);
